import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { useApiClient } from "./api-context.js";

interface DataTrustWarning {
  id: "acquisition_history" | "overhead_history" | "buygoods_historical_source" | "buygoods_live_source";
  title: string;
  summary: string;
  currentTreatment: string;
  recommendation: string;
  alternative: string;
  affectedCards: string[];
  fixHref: string;
  fixLabel: string;
  cardNotice: string;
}

function useDataTrustWarnings(enabled = true): DataTrustWarning[] {
  const api = useApiClient();
  const query = useQuery({
    queryKey: ["data-trust-warnings"],
    queryFn: () => api.get<{ warnings: DataTrustWarning[] }>("/verify/data-warnings"),
    enabled,
    staleTime: 5 * 60_000,
  });
  return query.data?.warnings ?? [];
}

export function DataTrustBanner(): React.ReactElement | null {
  const location = useLocation();
  const hidden = location.pathname.startsWith("/welcome")
    || location.pathname.startsWith("/onboarding")
    || location.pathname.startsWith("/agency")
    || location.pathname.startsWith("/admin");
  const warnings = useDataTrustWarnings(!hidden);
  if (hidden || warnings.length === 0) return null;

  return (
    <div style={styles.stack}>
      {warnings.map((warning) => (
        <div key={warning.id} role="alert" style={styles.wrap}>
          <div style={styles.title}>Needs attention: {warning.title}</div>
          <div style={styles.summary}>{warning.summary}</div>
          <div style={styles.affected}><b>Affected:</b> {warning.affectedCards.join(", ")}</div>
          <div style={styles.grid}>
            <div style={styles.safe}><b>What the dashboard does now</b><br />{warning.currentTreatment}</div>
            <div style={styles.recommend}><b>Recommended</b><br />{warning.recommendation}</div>
          </div>
          <div style={styles.actions}>
            <Link to={warning.fixHref} style={styles.cta}>{warning.fixLabel}</Link>
            <span style={styles.alternative}>{warning.alternative}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Small card-level companion to the large account warning above. */
export function DataTrustCardNotice({ card }: { card: string }): React.ReactElement | null {
  const warnings = useDataTrustWarnings().filter((item) => item.affectedCards.includes(card));
  if (warnings.length === 0) return null;
  return (
    <div style={styles.cardNoticeStack}>
      {warnings.map((warning) => (
        <div key={warning.id} role="status" style={styles.cardNotice}>
          <b>Needs attention:</b> {warning.title}. {warning.cardNotice}
        </div>
      ))}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    background: "color-mix(in srgb, var(--c2) 13%, var(--surface))",
    border: "2px solid color-mix(in srgb, var(--c2) 65%, var(--border))",
    borderRadius: "var(--r-md)",
    padding: "15px 17px",
    boxShadow: "0 4px 18px rgba(0,0,0,0.07)",
  },
  stack: { display: "grid", gap: 12, marginBottom: 20 },
  title: { color: "var(--c2)", fontSize: 15.5, fontWeight: 800, marginBottom: 5 },
  summary: { color: "var(--text)", fontSize: 13.5, lineHeight: 1.5, maxWidth: 920 },
  affected: { color: "var(--body)", fontSize: 12.5, lineHeight: 1.45, marginTop: 7 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 9, marginTop: 11 },
  safe: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "9px 11px", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 },
  recommend: { background: "color-mix(in srgb, var(--good) 7%, var(--surface))", border: "1px solid color-mix(in srgb, var(--good) 35%, var(--border))", borderRadius: 8, padding: "9px 11px", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 },
  actions: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 11 },
  cta: { background: "var(--accent)", color: "var(--accent-contrast)", padding: "7px 13px", borderRadius: 7, fontSize: 12.5, fontWeight: 700, textDecoration: "none" },
  alternative: { color: "var(--text-dim)", fontSize: 12 },
  cardNotice: { marginTop: 8, padding: "7px 9px", borderRadius: 7, background: "color-mix(in srgb, var(--c2) 10%, var(--surface))", border: "1px solid color-mix(in srgb, var(--c2) 45%, var(--border))", color: "var(--body)", fontSize: 11.5, lineHeight: 1.45 },
  cardNoticeStack: { display: "grid", gap: 6 },
};
